'use client';

import { useEffect, useRef } from 'react';

/**
 * Fail-open fade-in-on-scroll wrapper.
 *
 * Unlike the shared framer-motion `FadeInView`, this renders VISIBLE by default
 * (opacity:1 in SSR and on first client render), so text is never hidden when JS
 * is slow, blocked, or absent (crawlers, dev HMR failures over the tunnel, etc.).
 * The entrance is a progressive enhancement applied imperatively on mount: we only
 * hide elements still below the fold, then fade them in as they scroll into view -
 * so nothing already on screen ever flashes, and no JS => everything stays visible.
 */
export default function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
    if (inView) return; // already on screen - leave visible, no flash, no animation

    // Below the fold: hide instantly (off-screen, unseen), then transition in.
    el.style.transition = 'none';
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    void el.offsetHeight; // flush the hidden state before arming the transition
    el.style.transition = `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.style.opacity = '1';
            el.style.transform = 'none';
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={className} style={{ opacity: 1 }}>
      {children}
    </div>
  );
}
